import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { Badge } from "@/components/ui/badge";
import { LoadingTable } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { AdminConsoleFailureState } from "@/features/admin/admin-console-failure";
import { formatCheckedAt, useServiceExplorer } from "@/features/admin/use-service-explorer";
import type { ExplorerService, ServiceStatus } from "@/lib/admin-console-api";

/**
 * M1 — Services (ALL-5776, spec: product-design/specs/04-governance-evidence/
 * admin-support-diagnostics-spec.md). Whether each platform service is answering,
 * read from GET /admin/api/service-explorer: the admin console's probe snapshot
 * plus its service catalog. Read-only; the console's re-probe action is not
 * exposed, so the snapshot time is always shown.
 */

const STATUS_LABEL: Record<ServiceStatus, string> = { up: "Up", degraded: "Degraded", down: "Down", unknown: "Unknown" };
const STATUS_TONE: Record<ServiceStatus, "success" | "warning" | "danger" | "neutral"> = {
  up: "success",
  degraded: "warning",
  down: "danger",
  unknown: "neutral",
};
const STATUS_ORDER: Record<ServiceStatus, number> = { down: 0, degraded: 1, unknown: 2, up: 3 };

function matches(service: ExplorerService, query: string): boolean {
  if (!query) return true;
  const haystack = [service.name, service.id, service.module, service.owner, ...service.capabilities].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(query);
}

function ServiceDetail({ service }: { service: ExplorerService }) {
  return (
    <div className="grid gap-4 bg-bg px-4 py-4 text-sm md:grid-cols-2">
      <div className="flex flex-col gap-2">
        {service.purpose && <p className="text-ink">{service.purpose}</p>}
        {service.capabilities.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted">Capabilities</div>
            <p className="text-ink">{service.capabilities.join(", ")}</p>
          </div>
        )}
        {service.dependencies.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted">Depends on</div>
            <p className="text-ink">{service.dependencies.join(", ")}</p>
          </div>
        )}
      </div>
      <div>
        <div className="mb-1 text-xs font-semibold text-muted">Health checks</div>
        {service.probes.length === 0 ? (
          <p className="text-muted">This service has no endpoint the console can check, so its status comes from the catalog only.</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {service.probes.map((probe, index) => (
              <li key={`${probe.endpoint}-${index}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span className="min-w-0 break-words text-ink">{probe.endpoint}</span>
                <span className="flex items-center gap-2 text-xs text-muted">
                  {probe.httpStatus !== undefined && <span>HTTP {probe.httpStatus}</span>}
                  {probe.latencyMs !== undefined && <span>{probe.latencyMs} ms</span>}
                  {probe.error && <span className="font-mono">{probe.error}</span>}
                  <Badge variant={STATUS_TONE[probe.status]} size="sm">{STATUS_LABEL[probe.status]}</Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function AdminServicesPage() {
  const { state, reload } = useServiceExplorer();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | ServiceStatus>("all");
  const [open, setOpen] = useState<string | null>(null);

  const services = useMemo(() => {
    if (state.kind !== "ready") return [];
    const q = query.trim().toLowerCase();
    return state.explorer.services
      .filter((service) => (status === "all" || service.status === status) && matches(service, q))
      .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name));
  }, [state, query, status]);

  const checkedAt = state.kind === "ready" ? formatCheckedAt(state.explorer.checkedAt) : undefined;

  return (
    <div>
      <PageHeader
        title="Services"
        subtitle={checkedAt ? `Whether each platform service is answering. Last checked ${checkedAt}.` : "Whether each platform service is answering."}
        actions={
          <Link to="/admin/config-viewer" className="text-sm font-semibold text-primary hover:underline">
            View configuration
          </Link>
        }
      />

      {state.kind === "loading" && <LoadingTable rows={5} cols={4} label="Loading services" />}

      {state.kind === "failed" && <AdminConsoleFailureState failure={state.failure} subject="service health" onRetry={reload} />}

      {state.kind === "ready" && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard value={String(state.explorer.summary.serviceCount)} label="Services" />
            <StatCard value={String(state.explorer.summary.up)} label="Up" tone="success" />
            <StatCard value={String(state.explorer.summary.degraded)} label="Degraded" tone={state.explorer.summary.degraded > 0 ? "warning" : "default"} />
            <StatCard value={String(state.explorer.summary.down)} label="Down" tone={state.explorer.summary.down > 0 ? "danger" : "default"} />
          </div>

          <FilterBar>
            <FilterSearch value={query} onChange={setQuery} placeholder="Search name, module, owner, capability" ariaLabel="Search services" />
            <FilterSelect
              value={status}
              onChange={(value) => setStatus(value as "all" | ServiceStatus)}
              ariaLabel="Filter by status"
              options={[
                { value: "all", label: "All statuses" },
                { value: "down", label: "Down" },
                { value: "degraded", label: "Degraded" },
                { value: "up", label: "Up" },
              ]}
            />
          </FilterBar>

          {state.explorer.services.length === 0 ? (
            <div data-testid="services-empty" className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
              The console reported no services for this deployment.
            </div>
          ) : services.length === 0 ? (
            <div data-testid="services-filtered-empty" className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
              No services match these filters. Clear the search or choose all statuses.
            </div>
          ) : (
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg">
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Service</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Module</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Owner</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Endpoints</th>
                    <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service) => {
                    const expanded = open === service.id;
                    return (
                      <Fragment key={service.id}>
                        <tr className="border-b border-line" data-testid="service-row">
                          <td className="px-4 py-[9px] text-[13px] text-ink">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 text-left font-semibold text-ink hover:text-primary"
                              aria-expanded={expanded}
                              onClick={() => setOpen(expanded ? null : service.id)}
                            >
                              {expanded ? <ChevronDown aria-hidden className="size-4" /> : <ChevronRight aria-hidden className="size-4" />}
                              {service.name}
                            </button>
                          </td>
                          <td className="px-4 py-[9px] text-[12px] text-muted">{service.module ?? "—"}</td>
                          <td className="px-4 py-[9px] text-[12px] text-muted">{service.owner ?? "—"}</td>
                          <td className="px-4 py-[9px] text-[12px] text-muted">{service.endpointCount}</td>
                          <td className="px-4 py-[9px]">
                            <Badge variant={STATUS_TONE[service.status]} size="sm">{STATUS_LABEL[service.status]}</Badge>
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-line">
                            <td colSpan={5} className="p-0">
                              <ServiceDetail service={service} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </>
      )}
    </div>
  );
}
